import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cc`;

const CcLobby = ({ user, onGameStart }) => {
  const [queueSize, setQueueSize] = useState(0);
  const [matchedGameId, setMatchedGameId] = useState(null);
  const [dots, setDots] = useState('');
  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null);
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    const iv = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    if (matchedGameId && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      toast.success('Match trovato! Inizio partita...');
      onGameStart(matchedGameId);
      navigate(`/cc/game/${matchedGameId}`);
    }
  }, [matchedGameId, navigate, onGameStart]);

  const checkMatchmakingStatus = useCallback(async () => {
    try {
      const response = await axios.post(`${API}/game/join-matchmaking`, { user_id: user.user_id });
      if (response.data.matched) {
        setMatchedGameId(response.data.game_id);
      } else {
        setQueueSize(response.data.queue_size);
      }
    } catch (error) {
      console.error('Errore matchmaking CC', error);
    }
  }, [user.user_id]);

  const refreshQueueSize = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/game/queue-size`);
      setQueueSize(res.data.queue_size || 0);
    } catch(e) {}
  }, []);

  const subscribeToQueue = useCallback(() => {
    const ch = supabase.channel('cc_matchmaking_queue_updates')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cc_matchmaking_queue' }, () => {
        refreshQueueSize();
        checkMatchmakingStatus();
      }).subscribe();
    return () => { ch.unsubscribe(); supabase.removeChannel(ch); };
  }, [checkMatchmakingStatus, refreshQueueSize]);

  const subscribeToGameCreation = useCallback(() => {
    const ch = supabase.channel('cc_game_sessions_creation')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'cc_game_sessions' }, (payload) => {
        const players = payload.new?.players || [];
        if (players.some(p => p.user_id === user.user_id)) setMatchedGameId(payload.new.id);
      }).subscribe();
    return () => { ch.unsubscribe(); supabase.removeChannel(ch); };
  }, [user.user_id]);

  useEffect(() => {
    checkMatchmakingStatus();
    pollingIntervalRef.current = setInterval(checkMatchmakingStatus, 2000);
    const unsubQ = subscribeToQueue();
    const unsubG = subscribeToGameCreation();
    return () => {
      clearInterval(pollingIntervalRef.current);
      unsubQ(); unsubG();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CARD_ICONS = ['🪙','🎯','🎱','🎡','💸','🃏'];

  return (
    <div style={{
      minHeight:'100dvh',
      background:'linear-gradient(135deg,#0a0a1a 0%,#1a0a2e 45%,#0a0a1a 100%)',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      padding:'20px 16px', position:'relative', overflow:'hidden',
    }}>
      {CARD_ICONS.map((icon,i) => (
        <motion.div key={i}
          style={{ position:'absolute', left:`${10+i*15}%`, top:`${10+Math.sin(i)*30}%`,
            fontSize:24+i*4, pointerEvents:'none', zIndex:0 }}
          animate={{ y:[0,-16,0], rotate:[0,10,-10,0], opacity:[0.08,0.25,0.08] }}
          transition={{ duration:3+i*0.5, repeat:Infinity, delay:i*0.4 }}>
          {icon}
        </motion.div>
      ))}

      <button onClick={() => navigate('/games')} style={{
        position:'fixed', top:14, left:16, zIndex:50,
        background:'rgba(10,5,20,0.85)', backdropFilter:'blur(8px)',
        border:'1px solid rgba(124,58,237,0.3)', borderRadius:10,
        padding:'6px 14px', color:'#c4b5fd', fontSize:13, fontWeight:700, cursor:'pointer',
      }}>← Survival Games</button>

      <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
        style={{ position:'relative', zIndex:1, width:'100%', maxWidth:440 }}>

        <div style={{ textAlign:'center', marginBottom:12 }}>
          <motion.div animate={{ rotate:[0,5,-5,0], scale:[1,1.1,1] }} transition={{ duration:2, repeat:Infinity }}
            style={{ fontSize:48 }}>🎰</motion.div>
        </div>

        <div style={{
          background:'linear-gradient(160deg,rgba(20,10,40,0.98),rgba(10,5,20,0.99))',
          borderRadius:24, border:'1px solid rgba(124,58,237,0.3)',
          boxShadow:'0 0 0 1px rgba(0,0,0,0.8),0 24px 60px rgba(0,0,0,0.9)',
          padding:'36px 32px', position:'relative', overflow:'hidden',
        }}>
          <motion.div animate={{ opacity:[0.3,0.8,0.3] }} transition={{ duration:2.5, repeat:Infinity }}
            style={{ position:'absolute', inset:0, border:'1px solid rgba(124,58,237,0.5)', borderRadius:24, pointerEvents:'none' }} />

          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{ fontSize:9, fontWeight:800, letterSpacing:'0.2em', color:'rgba(196,181,253,0.5)', textTransform:'uppercase', marginBottom:10 }}>
              Crazy Cards
            </div>
            <h1 style={{ fontFamily:"'Cinzel',serif", fontSize:26, fontWeight:900, margin:'0 0 6px',
              background:'linear-gradient(to bottom,#fff,#a855f7)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              Sala d'attesa
            </h1>
            <p style={{ margin:0, fontSize:13, color:'rgba(255,255,255,0.35)' }}>
              Cercando avversari{dots}
            </p>
          </div>

          <div style={{ marginBottom:24 }}>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.3)', textTransform:'uppercase', textAlign:'center', marginBottom:14 }}>
              Giocatori in attesa
            </div>
            <div style={{ display:'flex', justifyContent:'center', gap:10 }}>
              {[...Array(4)].map((_,i) => (
                <motion.div key={i}
                  animate={i < queueSize ? { opacity:[0.7,1,0.7] } : {}}
                  transition={{ duration:1.5, repeat:Infinity }}
                  style={{
                    width:48, height:48, borderRadius:10, fontSize:22,
                    background: i < queueSize ? 'linear-gradient(145deg,#5b21b6,#4c1d95)' : 'rgba(255,255,255,0.04)',
                    border:`1.5px solid ${i < queueSize ? '#7c3aed' : 'rgba(255,255,255,0.08)'}`,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    boxShadow: i < queueSize ? '0 0 16px rgba(124,58,237,0.5)' : 'none',
                  }}>
                  {i < queueSize ? '🃏' : '·'}
                </motion.div>
              ))}
            </div>
            <div style={{ textAlign:'center', marginTop:12 }}>
              <span style={{ fontFamily:"'Cinzel',serif", fontSize:32, fontWeight:900,
                background:'linear-gradient(to bottom,#fff,#a855f7)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
                {queueSize}
              </span>
              <span style={{ fontSize:18, color:'rgba(168,85,247,0.4)', fontWeight:700 }}> / 4</span>
            </div>
          </div>

          <div style={{ height:3, borderRadius:2, background:'rgba(255,255,255,0.05)', overflow:'hidden' }}>
            <motion.div animate={{ x:['-100%','200%'] }} transition={{ duration:1.8, repeat:Infinity, ease:'easeInOut' }}
              style={{ height:'100%', width:'40%', background:'linear-gradient(to right,transparent,#7c3aed,transparent)', borderRadius:2 }} />
          </div>

          <div style={{ textAlign:'center', marginTop:16, fontSize:12, color:'rgba(255,255,255,0.25)' }}>
            Connesso come <span style={{ color:'#c4b5fd', fontWeight:700 }}>{user.username}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CcLobby;
